"use client";
import { useState, useEffect } from "react";
import { Button } from "@/app/components/shadcn/button";
import { Heart, Loader2, Check, Share2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/shadcn/popover";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useCart from "@/app/lib/features/cart/cart.hook";
import useFavorite from "@/app/lib/features/favorite/favorite.hook";
import useModal from "@/app/lib/features/modal/modal.hook";
import {
  trackProductAddToCart,
  trackProductFavorite,
  trackInteraction,
} from "@/app/lib/tracking/interactions";

interface ProductActionsProps {
  selectedColorIndex: number;
  selectedSizeIndex: number;
}

const ProductActions = ({
  selectedColorIndex,
  selectedSizeIndex,
}: ProductActionsProps) => {
  const router = useRouter();
  const { product, updateVariantFavorite } = useProductDetail();
  const { addToCart } = useCart();
  const { toggleFavorite, isToggling } = useFavorite();
  const { createModal } = useModal();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!product) return null;

  const productId = product.id;

  const selectedColor = product.variants_by_color?.[selectedColorIndex];
  // Favori durumu doğrudan API verisinden: seçili rengin is_favorited'ı.
  // Ürün seviyesindeki is_favorited kullanılmıyor — pembeyi favorileyince
  // sarının da dolu görünmesinin sebebi oydu.
  const favoriteVariantId = selectedColor?.variant_id ?? product.variant_id;
  const isFav = selectedColor?.is_favorited ?? product.is_favorited ?? false;
  const isTogglingFav = isToggling(productId);

  const selectedSize =
    selectedSizeIndex !== -1
      ? selectedColor?.sizes?.[selectedSizeIndex]
      : undefined;
  const isOutOfStock = selectedSize?.stock === 0;
  const hasAnyStock = selectedColor?.sizes?.some((s) => s.stock > 0) ?? false;

  const execAddToCart = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;

    setIsAddingToCart(true);
    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      {
        name: product.name,
        price: size.price,
        brand: product.brand_name ?? undefined,
        affiliation: product.seller?.name,
        category: product.category?.name,
      },
    );

    if (result.success) {
      trackProductAddToCart(productId);
      createModal("added-to-cart", {
        image: color?.primary_image ?? product.images?.[0] ?? "",
        name: product.internet_product_description || product.name,
        colorName: color?.color_name,
        sizeName: size.size,
        // Fiyat API cevabından — sepete gerçekten hangi tutarla eklendiyse o.
        price: `${(result.price ?? size.price).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`,
      });
      setCartAdded(true);
      setTimeout(() => {
        setCartAdded(false);
        setIsAddingToCart(false);
      }, 2000);
    } else {
      toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
      setIsAddingToCart(false);
    }
  };

  const execBuyNow = async (colorIdx: number, sizeIdx: number) => {
    const color = product.variants_by_color?.[colorIdx];
    const size = color?.sizes?.[sizeIdx];
    if (!size) return;

    const result = await addToCart(
      { product_id: productId, variant_id: size.id, quantity: 1 },
      false,
      {
        name: product.name,
        price: size.price,
        brand: product.brand_name ?? undefined,
        affiliation: product.seller?.name,
        category: product.category?.name,
      },
    );

    if (result.success) {
      router.push("/sepet");
    } else {
      toast.error(result.message ?? "Sepete eklenirken bir hata oluştu");
    }
  };

  const openSizePicker = (action: "addToCart" | "buyNow") => {
    createModal("size-picker", {
      initialColorIndex: selectedColorIndex,
      initialSizeIndex: selectedSizeIndex,
      onConfirm: (colorIdx: number, sizeIdx: number) => {
        if (action === "addToCart") execAddToCart(colorIdx, sizeIdx);
        else execBuyNow(colorIdx, sizeIdx);
      },
    });
  };

  const handleBuyNow = () => {
    if (selectedSizeIndex === -1) {
      openSizePicker("buyNow");
      return;
    }
    execBuyNow(selectedColorIndex, selectedSizeIndex);
  };

  const handleAddToCart = () => {
    if (selectedSizeIndex === -1) {
      openSizePicker("addToCart");
      return;
    }
    execAddToCart(selectedColorIndex, selectedSizeIndex);
  };

  const handleToggleFavorite = async () => {
    const success = await toggleFavorite(productId, favoriteVariantId);
    if (success) {
      // Tek kaynak güncellenir: Redux'taki variants_by_color kaydı.
      updateVariantFavorite(favoriteVariantId, !isFav);
      if (!isFav) trackProductFavorite(productId);
      toast.success(isFav ? "Favorilerden çıkarıldı" : "Favorilere eklendi");
    } else {
      toast.error("Bir hata oluştu");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      trackInteraction("product", productId, "click");
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const shareLinks =
    product.share?.dropdown.items.map((item) => {
      const getAction = () => {
        if (item.type === "copy") return handleCopyLink;
        return () => {
          const encodedUrl = encodeURIComponent(window.location.href);
          const urlMap: Record<string, string> = {
            whatsapp: `https://wa.me/?text=${encodedUrl}`,
            telegram: `https://t.me/share/url?url=${encodedUrl}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            x: `https://x.com/intent/tweet?url=${encodedUrl}`,
            email: `mailto:?subject=Bu ürünü kontrol et!&body=${encodedUrl}`,
          };
          trackInteraction("product", productId, "click");
          window.open(urlMap[item.url] || item.url, "_blank");
        };
      };
      return {
        name: item.type === "copy" && isCopied ? "Kopyalandı!" : item.title,
        iconUrl: item.icon,
        action: getAction(),
        isCopyAction: item.type === "copy",
      };
    }) ?? [];

  return (
    <>
      <div className="hidden sm:flex gap-3">
        <Button
          type="button"
          variant="outline-main"
          size="xl"
          className="border-2 flex-1 px-2 sm:px-4 text-sm sm:text-base"
          onClick={handleBuyNow}
          disabled={isOutOfStock || !hasAnyStock}
        >
          Şimdi Al
        </Button>
        <Button
          type="button"
          size="xl"
          variant="orange"
          className="flex-1 px-2 sm:px-4 text-sm sm:text-base"
          onClick={handleAddToCart}
          disabled={isAddingToCart || isOutOfStock || !hasAnyStock}
        >
          {isAddingToCart ? (
            cartAdded ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Sepete </span>Eklendi
              </>
            ) : (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="hidden sm:inline">Sepete </span>Ekleniyor
              </>
            )
          ) : (
            <>
              <span className="hidden sm:inline">Sepete Ekle</span>
              <span className="sm:hidden">Sepete Ekle</span>
            </>
          )}
        </Button>
        <Button
          type="button"
          size="icon-xl"
          className={`rounded-full shrink-0 ${isFav ? "bg-red-500 hover:bg-red-600 text-white" : ""}`}
          variant={isFav ? "default" : "outline"}
          onClick={handleToggleFavorite}
          disabled={isTogglingFav}
        >
          {isTogglingFav ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Heart className={isFav ? "fill-current" : ""} />
          )}
        </Button>
        {mounted && product.share && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="icon-xl"
                variant="outline"
                className="rounded-full shrink-0 lg:hidden"
              >
                <Share2 />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <div className="space-y-1">
                <div className="px-3 py-2 text-sm font-medium text-gray-900 border-b">
                  {product.share.dropdown.text}
                </div>
                {shareLinks.map((link, index) => (
                  <button
                    key={index}
                    onClick={link.action}
                    className="cursor-pointer w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors text-left"
                  >
                    {link.isCopyAction && isCopied ? (
                      <Check size={16} className="text-green-600" />
                    ) : link.iconUrl ? (
                      <Image
                        src={link.iconUrl}
                        alt={link.name}
                        width={16}
                        height={16}
                        className="w-4 h-4 object-contain"
                      />
                    ) : (
                      <Share2 size={16} className="text-gray-600" />
                    )}
                    <span>{link.name}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
};

export default ProductActions;
